import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Eye, EyeOff, Sparkles, Calendar, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CATEGORY_LABELS, ROUTE_PATHS } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/Layout';
import { supabase } from '@/integrations/supabase/client';

export default function ForumAskPage() {
  const { isLoggedIn, userId } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState<string>('');
  const [question, setQuestion] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [userCredits, setUserCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  // Load user credits
  useEffect(() => {
    if (userId) {
      loadUserCredits();
    }
  }, [userId]);

  const loadUserCredits = async () => {
    try {
      const { data } = await supabase
        .from('user_credits_2026_03_28')
        .select('credits_remaining')
        .eq('user_id', userId)
        .single();
      
      setUserCredits(data?.credits_remaining || 0);
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!category || category === 'none') {
      setError('Molimo izaberite kategoriju');
      return;
    }

    if (!question.trim()) {
      setError('Molimo unesite pitanje');
      return;
    }

    if (question.trim().length < 20) {
      setError('Pitanje mora imati najmanje 20 karaktera');
      return;
    }

    // Check if user has credits
    if (userCredits < 1) {
      setError('Nemate dovoljno kredita. Molimo kupite kredite da biste postavili pitanje.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Call Edge Function to submit question
      const { data, error } = await supabase.functions.invoke('submit_question_simple_2026_03_29', {
        body: {
          question: question.trim(),
          category: category,
          isAnonymous: isAnonymous,
          birthDate: birthDate || null,
          birthTime: birthTime || null,
          birthPlace: birthPlace.trim() || null,
        },
      });

      if (error) throw error;

      // Reload credits after successful submission
      await loadUserCredits();
      
      // Navigate to My Questions page
      navigate(ROUTE_PATHS.FORUM_MY_QUESTIONS);
    } catch (err) {
      setError('Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show login prompt if not logged in
  if (!isLoggedIn) {
    return (
      <Layout>
        <div className="min-h-screen bg-background pt-[140px] flex items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Prijavite se</CardTitle>
              <CardDescription>
                Morate biti prijavljeni da biste postavili pitanje.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate(ROUTE_PATHS.FORUM_CREDITS)} className="w-full">
                Idite na Prijavu
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-accent/5 pointer-events-none" />
      
      <div className="container mx-auto px-4 py-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4"
            >
              <Sparkles className="w-8 h-8 text-primary" />
            </motion.div>
            <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Postavite Pitanje
            </h1>
            <p className="text-muted-foreground text-lg">
              Dobijte odgovor od profesionalnog astrologa
            </p>
          </div>

          <Card className="border-primary/20 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5 text-primary" />
                    Novo Pitanje
                  </CardTitle>
                  <CardDescription>
                    Popunite formu ispod i naš astrolog će vam odgovoriti u najkraćem roku
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Preostalo kredita:</p>
                  <p className="text-2xl font-bold text-primary">{userCredits}</p>
                  {userCredits < 1 && (
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => navigate(ROUTE_PATHS.FORUM_CREDITS)}
                      className="text-xs"
                    >
                      Kupi kredite
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategorija *</Label>
                  <Select value={category || 'none'} onValueChange={setCategory}>
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Izaberite kategoriju" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Izaberite kategoriju</SelectItem>
                      {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="question">Vaše Pitanje *</Label>
                  <Textarea
                    id="question"
                    placeholder="Opišite detaljno vaše pitanje... (minimum 20 karaktera)"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={8}
                    className="resize-none"
                  />
                  <p className="text-sm text-muted-foreground">
                    {question.length} / 20 karaktera minimum
                  </p>
                </div>

                {/* Birth Data Fields */}
                <Alert className="border-primary/50 bg-primary/5">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <AlertDescription>
                    <strong>Važno za tačan odgovor:</strong> Datum, vreme i mesto rođenja omogućavaju preciznu astrološku analizu vašeg natalnog horoskopa.
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="birthDate" className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Datum rođenja *
                    </Label>
                    <Input
                      id="birthDate"
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="birthTime" className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Vreme rođenja *
                    </Label>
                    <Input
                      id="birthTime"
                      type="time"
                      value={birthTime}
                      onChange={(e) => setBirthTime(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="birthPlace" className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Mesto rođenja *
                    </Label>
                    <Input
                      id="birthPlace"
                      type="text"
                      placeholder="Beograd, Srbija"
                      value={birthPlace}
                      onChange={(e) => setBirthPlace(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
                    <div className="flex items-center gap-3">
                      {isAnonymous ? (
                        <EyeOff className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <Eye className="w-5 h-5 text-primary" />
                      )}
                      <div>
                        <Label htmlFor="anonymous" className="cursor-pointer">
                          Anonimno pitanje
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {isAnonymous
                            ? 'Vaše ime neće biti prikazano'
                            : 'Vaše ime će biti vidljivo'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="anonymous"
                      checked={isAnonymous}
                      onCheckedChange={setIsAnonymous}
                    />
                  </div>

                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(ROUTE_PATHS.FORUM)}
                    className="flex-1"
                  >
                    Otkaži
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                        />
                        Šaljem...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Pošalji Pitanje (1 kredit)
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-center"
          >
            <p className="text-sm text-muted-foreground">
              Nemate kredita?{' '}
              <button
                onClick={() => navigate(ROUTE_PATHS.FORUM_CREDITS)}
                className="text-primary hover:underline font-medium"
              >
                Kupite paket kredita
              </button>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}